/**
 * Caption generators for Share Studio.
 * Swedish copy, per entity type and platform.
 */

import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import { derivePreviewText } from '@/lib/text';
import type { ShareTarget, SharePlatform, ActivityShareTarget } from './ShareTarget';

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return format(new Date(iso), "d MMMM yyyy 'kl' HH:mm", { locale: sv });
  } catch {
    return null;
  }
}

function activityCta(bookingMode: string): string {
  if (bookingMode === 'required') return 'Boka din plats på Farmly 👇';
  if (bookingMode === 'optional') return 'Anmäl ditt intresse på Farmly 👇';
  return 'Läs mer på Farmly 👇';
}

function activityDateLine(t: ActivityShareTarget): string {
  const dateStr = formatDate(t.startDate);
  if (dateStr) return `📅 ${dateStr}`;
  if (t.timingMode === 'open' || t.timingMode === 'anytime') return '📅 Öppet under säsong';
  if (t.timingMode === 'season') return '📅 Säsongsaktivitet';
  return '';
}

/* ── Short caption ─────────────────────── */

function activityShort(t: ActivityShareTarget, platform: SharePlatform): string {
  const lines: string[] = [t.title, ''];
  const dateLine = activityDateLine(t);
  if (dateLine) lines.push(dateLine);
  const price = getPriceCopy({ is_free: t.isFree, price_ore: t.priceOre });
  if (price.primary) lines.push(`💰 ${price.primary}`);
  lines.push('');
  lines.push(activityCta(t.bookingMode));
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function activityLong(t: ActivityShareTarget, platform: SharePlatform): string {
  const lines: string[] = [t.title, ''];
  const dateLine = activityDateLine(t);
  if (dateLine) lines.push(dateLine);
  const price = getPriceCopy({ is_free: t.isFree, price_ore: t.priceOre });
  if (price.primary) lines.push(`💰 ${price.primary}`);
  if (t.venueText) lines.push(`📍 ${t.venueText}`);

  // Description excerpt
  const preview = derivePreviewText(t.descriptionHtml, 140);
  if (preview) {
    lines.push('', preview);
  }

  lines.push('');
  lines.push(activityCta(t.bookingMode));
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function producerShort(t: ShareTarget, platform: SharePlatform): string {
  const lines = [`Välkommen till ${t.producerName} 🌿`];
  lines.push('');
  lines.push('Upptäck vår gård på Farmly');
  lines.push('farmly.se 👇');
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function producerLong(t: ShareTarget, platform: SharePlatform): string {
  const lines = [`Välkommen till ${t.producerName} 🌿`];
  lines.push('');
  if (t.type === 'producer' && t.tagline) lines.push(t.tagline, '');
  if (t.type === 'producer' && t.placeName) lines.push(`📍 ${t.placeName}`);
  lines.push('');
  lines.push('Upptäck mer på Farmly 👇');
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function shopShort(t: ShareTarget, _platform: SharePlatform): string {
  const lines = [`Gårdsbutiken hos ${t.producerName} 🛒`];
  lines.push('');
  lines.push('Handla lokalt direkt från gården 🌿');
  lines.push('Se utbudet på farmly.se 👇');
  if (_platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function shopLong(t: ShareTarget, _platform: SharePlatform): string {
  const lines = [`Välkommen till gårdsbutiken hos ${t.producerName} 🛒`];
  lines.push('');
  if (t.type === 'shop') {
    if (t.shopIntro) lines.push(t.shopIntro, '');
    if (t.placeName) lines.push(`📍 ${t.placeName}`);
    if (t.openingSnippet) lines.push(`🕐 ${t.openingSnippet}`);
  }
  lines.push('');
  lines.push('Handla lokalt direkt från gården');
  lines.push('Se utbudet på farmly.se 👇');
  if (_platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function productShort(t: ShareTarget, platform: SharePlatform): string {
  const lines: string[] = [t.title, ''];
  if (t.type === 'product') {
    const price = (t.priceOre / 100).toLocaleString('sv-SE') + ' kr';
    lines.push(`💰 ${price}`);
  }
  lines.push('');
  lines.push(`Finns hos ${t.producerName} på Farmly 🌿`);
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

function productLong(t: ShareTarget, platform: SharePlatform): string {
  const lines: string[] = [t.title, ''];
  if (t.type === 'product') {
    const price = (t.priceOre / 100).toLocaleString('sv-SE') + ' kr';
    lines.push(`💰 ${price}`);
    if (t.placeName) lines.push(`📍 ${t.placeName}`);
    if (t.deliverySnippet) lines.push(`🚚 ${t.deliverySnippet}`);
    if (t.descriptionExcerpt) lines.push('', t.descriptionExcerpt);
  }
  lines.push('');
  lines.push(`Från ${t.producerName} på Farmly`);
  lines.push('Beställ via farmly.se 👇');
  if (platform === 'instagram') {
    lines.push('', '🔗 Länk i bio');
  }
  return lines.join('\n');
}

/* ── Public API ────────────────────────── */

export type CaptionVariant = 'short' | 'long';

export function generateCaption(
  target: ShareTarget,
  platform: SharePlatform,
  variant: CaptionVariant = 'short',
): string {
  switch (target.type) {
    case 'activity':
      return variant === 'long'
        ? activityLong(target, platform)
        : activityShort(target, platform);
    case 'producer':
      return variant === 'long'
        ? producerLong(target, platform)
        : producerShort(target, platform);
    case 'shop':
      return variant === 'long'
        ? shopLong(target, platform)
        : shopShort(target, platform);
    case 'product':
      return variant === 'long'
        ? productLong(target, platform)
        : productShort(target, platform);
  }
}
